import type { CourseSettings } from './useCourse';

// Reading the course-level display settings off a raw CourseSettings message.
//
// The image size override says how large images should DISPLAY across the whole
// course. It never changes what any image stores: ContentUnit.image_size stays
// as its author set it, and the editor keeps showing that.
//
// `authored` is the zero value and means "respect every image's own size", so a
// course that has never touched this setting behaves exactly as it did before
// the setting existed.

export type CourseImageSizeOverride = 'authored' | 'large' | 'medium' | 'small';

function getSettingFlag(
  settings: CourseSettings | null | undefined,
  snakeKey: string,
  camelKey: string,
) {
  const value = settings?.[snakeKey] ?? settings?.[camelKey];
  return typeof value === 'boolean' ? value : false;
}

/**
 * Both homepage settings are stored INVERTED — as "disable" flags — while every
 * control presents them positively. Reading them through these two helpers keeps
 * that inversion in one place; it now has two readers, the homepage that renders
 * with it and the dialog that edits it.
 */
export function isAutoNumberingDisabled(settings: CourseSettings | null | undefined) {
  return getSettingFlag(settings, 'disable_auto_numbering', 'disableAutoNumbering');
}

export function isDueSoonSectionDisabled(settings: CourseSettings | null | undefined) {
  return getSettingFlag(
    settings,
    'disable_due_soon_section',
    'disableDueSoonSection',
  );
}

/** The three sizes an override can force. `authored` is the absence of one. */
export type ForcedImageSize = Exclude<CourseImageSizeOverride, 'authored'>;

// Numbering matches CourseSettings.ImageSizeOverride in the proto, which in turn
// matches ContentUnit.ImageSize — so an override maps onto a display size
// without a translation step that could be got wrong.
const OVERRIDE_BY_VALUE: Record<string, CourseImageSizeOverride> = {
  '0': 'authored',
  '1': 'small',
  '2': 'medium',
  '3': 'large',
  AUTHORED: 'authored',
  LARGE: 'large',
  MEDIUM: 'medium',
  SMALL: 'small',
};

function normalizeBooleanish(value: unknown): boolean {
  if (typeof value === 'boolean') {
    return value;
  }
  if (typeof value === 'number') {
    return value === 1;
  }
  if (typeof value === 'string') {
    return value.trim().toLowerCase() === 'true';
  }
  return false;
}

/**
 * The course's image size override.
 *
 * Anything unrecognized resolves to `authored`. Failing safe here means
 * rendering what the author intended rather than imposing a size nobody asked
 * for — a wrong guess would resize every figure in the course.
 */
export function resolveImageSizeOverride(
  settings: CourseSettings | null | undefined,
): CourseImageSizeOverride {
  const raw = settings?.image_size_override ?? settings?.imageSizeOverride;

  if (raw === null || raw === undefined) {
    return 'authored';
  }

  const key =
    typeof raw === 'number' ? String(raw) : String(raw).trim().toUpperCase();

  return OVERRIDE_BY_VALUE[key] ?? 'authored';
}

/**
 * Whether an override also reaches images inside questions — prompts, rich
 * content answer options, and hint and solution panels.
 *
 * Meaningless on its own: with no override in force there is nothing to extend.
 */
export function imageSizeOverrideIncludesQuestions(
  settings: CourseSettings | null | undefined,
): boolean {
  return normalizeBooleanish(
    settings?.image_size_override_includes_questions ??
      settings?.imageSizeOverrideIncludesQuestions,
  );
}

/**
 * The override to apply on a given surface, already narrowed by scope.
 *
 * Returning `authored` for questions when the scope setting is off is what keeps
 * the scope decision at the render boundary instead of inside the image
 * renderer, which has no business knowing what a question is.
 */
export function resolveImageSizeOverrideForSurface(
  settings: CourseSettings | null | undefined,
  surface: 'chapter' | 'question',
): CourseImageSizeOverride {
  const override = resolveImageSizeOverride(settings);

  if (override === 'authored') {
    return 'authored';
  }

  return surface === 'question' && !imageSizeOverrideIncludesQuestions(settings)
    ? 'authored'
    : override;
}
